import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);

  useEffect(() => {
    // Simulate search results
    if (query) {
      const mockResults = [
        {
          title: 'AI & Machine Learning Services',
          description: 'Implement intelligent automation, predictive analytics, and custom AI models to streamline operations.',
          url: '/services/ai-machine-learning',
          type: 'Service'
        },
        {
          title: 'Custom Software Development',
          description: 'Bespoke applications built to your exact specifications with full integration capabilities.',
          url: '/services/custom-software',
          type: 'Service'
        },
        {
          title: 'Healthcare & Life Sciences Solutions',
          description: 'Transform healthcare delivery with AI-powered diagnostics and patient care systems.',
          url: '/solutions/healthcare-life-sciences',
          type: 'Solution'
        },
        {
          title: 'About Abacies Logiciels',
          description: 'Learn about our journey from a visionary startup to a global leader in enterprise technology.',
          url: '/about',
          type: 'Company'
        },
        {
          title: 'Contact Us',
          description: 'Get in touch with our expert team for consultation and project inquiries.',
          url: '/contact',
          type: 'Page'
        }
      ];

      // Filter results based on query
      const filtered = mockResults.filter(result => 
        result.title.toLowerCase().includes(query.toLowerCase()) ||
        result.description.toLowerCase().includes(query.toLowerCase())
      );

      setResults(filtered.length ? filtered : mockResults);
    }
  }, [query]);

  return (
    <>
      <SEO 
        title={`Search Results${query ? ` for "${query}"` : ''}`}
        description={`Search results for ${query || 'all content'} on Abacies Logiciels website.`}
        keywords="search, find, results, Abacies content"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
              Search Results {query && <span className="text-titanium">for "{query}"</span>}
            </h1>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                defaultValue={query}
                className="flex-1 px-4 py-3 border border-platinum rounded-lg focus:outline-none focus:border-primary-red"
                placeholder="Enter your search query..."
              />
              <button className="btn-primary">Search</button>
            </div>
            {query && (
              <p className="mt-4 text-titanium">
                Found {results.length} result{results.length !== 1 ? 's' : ''} for your search
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Search Results */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            {results.length > 0 ? (
              <div className="space-y-6">
                {results.map((result, index) => (
                  <div key={index} className="card hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Link to={result.url} className="text-xl font-semibold text-charcoal hover:text-primary-red transition-colors">
                          {result.title}
                        </Link>
                        <p className="mt-2 text-titanium">{result.description}</p>
                        <div className="mt-3 flex items-center space-x-4">
                          <span className="px-3 py-1 bg-blush-light/40 text-primary-red text-sm rounded-full">
                            {result.type}
                          </span>
                          <Link to={result.url} className="text-primary-red hover:underline">
                            View Page →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <svg className="w-24 h-24 text-cool-grey mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h2 className="text-2xl font-semibold text-charcoal mb-2">No results found</h2>
                <p className="text-titanium mb-6">Try searching with different keywords or browse our services</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/services" className="btn-primary">Browse Services</Link>
                  <Link to="/solutions" className="btn-secondary">View Solutions</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Popular Searches */}
      <section className="py-16 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-charcoal">Popular Searches</h2>
            <div className="flex flex-wrap gap-3">
              {['AI Solutions', 'Machine Learning', 'Custom Software', 'Cloud Services', 'Digital Transformation', 
                'Healthcare Solutions', 'Financial Services', 'Data Analytics', 'Cybersecurity', 'Contact Us'].map((term, index) => (
                <Link 
                  key={index} 
                  to={`/search?q=${encodeURIComponent(term)}`}
                  className="px-4 py-2 bg-white border border-platinum rounded-lg hover:border-primary-red hover:text-primary-red transition-all duration-300"
                >
                  {term}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SearchResults;
